import { ConstantsBrowserLocalCache } from "../constants/ConstantsBrowserLocalCache";

export enum StorageType {
    Dynamic = 1,
    SessionStorage = 2, // should not be used anymore - in case one closed a tab (within browser) a new session will be created - see bug #592
    LocalStorage = 3,
    Cookie = 4 // alternative to SessionStorage - exists for browser-session - will not be removed if single tab is closed
};

export class LocalCacheHelper {

    /*
        Looks a bit complicated now because we need to support SessionStorage and LocalStorage and Cookie.
        (Work-Item 408, User-Story 271)
        (Bugfix 592)

        KeyCpbxEnvName and KeyCpbxProxyUrl will be stored to LocalStorage always.
        KeyCpbxUserName, KeyCpbxUserBasicAuthString and KeyCpbxUserProfileMe may be stored to
        SessionStorage OR LocalStorage OR Cookie (depends on users-choice at login-page).
    */

    public static DoesLocalCacheKeysForCbpxLoginExists(): boolean {
        console.log("DoesLocalCacheKeysForCbpxLoginExists");
        let isLoggedIn: boolean = true;

        if (this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserName) === null ||
            this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserBasicAuthString) === null ||
            this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserProfileMe) === null
            // this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxEnvName) === null ||
            // this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxProxyUrl) === null
        ) {
            isLoggedIn = false;
            this.RemoveLocalCacheKeysForCbpxLogin();
        }

        console.log("returning isLoggedIn='" + isLoggedIn + "'");

        return isLoggedIn;
    }

    // used by internal check if login-data exists (and implicitly by logout-functionality)
    public static RemoveLocalCacheKeysForCbpxLogin(): void {
        console.log("RemoveLocalCacheKeysForCbpxLogin");
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserName);
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserBasicAuthString);
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserProfileMe);
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserIvString);
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyM365UserObjectId);
        this.RemoveLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserType);
    }

    // used by logout-functionality
    public static RemoveLocalCacheKeysForCbpxAll(): void {
        console.log("RemoveLocalCacheKeysForCbpxAll");
        this.RemoveLocalCacheKeysForCbpxLogin();
        this.RemoveLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxEnvName, StorageType.LocalStorage);
        this.RemoveLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxProxyUrl, StorageType.LocalStorage);
        this.RemoveLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyAddressBookRecords, StorageType.LocalStorage);
        this.RemoveLocalStorageForCbpxAddressbook();
        this.RemoveLocalStorageForToken();
    }

    public static RemoveLocalStorageForToken() {
        this.RemoveLocalStorageItem("CpbxCiscoTokenForGroup");
    }

    // used for remove Addressbook from LocalStorage
    public static RemoveLocalStorageForCbpxAddressbook() {
        console.log("RemoveLocalStorageForCbpxAddressbook");
        for (let i = 0; i <= localStorage.length; i++) {

            // the variable needs to be overwritten because deletion works asynchronously
            const temp = new RegExp(ConstantsBrowserLocalCache.KeyAddressBookRecordsPartPrefix + ".*", "g");
            const keyName:any = localStorage.key(i);
            if ( temp.test(keyName) ) {
                console.log("remove item: ", keyName);

                // Since the functions are executed asynchronously, the setTimeout has been added.
                setTimeout(() => this.RemoveLocalStorageItem(keyName), 1);
            }
        }
    }

    private static GetLocalStorageItem(key: string): any {
        return this.GetLocalStorageItemDynamic(key, StorageType.Dynamic);
    }

    private static GetLocalStorageItemDynamic(key: string, storageType: StorageType): any {
        if (storageType === StorageType.Dynamic) {
            // console.log("storageType is dynamic");
            // first try to get item from cookie-storage, then get it from localstorage
            // let v = sessionStorage.getItem(key);
            let v: string | null;
            if (this.DoesCookieKeyExists(key) === true) {
                // console.log("cookie-key '" + key + "' does exist > so get its value now and return it");
                v = this.GetCookieItemValue(key);
            } else {
                // console.log("cookie-key '" + key + "' does NOT exist > try to get value from sessionstorage now");
                v = sessionStorage.getItem(key);
                if (v === null) {
                    // console.log("cookie and sessionstorage-key '" + key + "' does NOT exist > try to get value from localsorage now");
                    v = localStorage.getItem(key);
                }
            }
            // console.log("finally return value = '" + v + "' now");
            return v;
        } else if (storageType === StorageType.Cookie) {
            // console.log("storageType is cookie");
            return this.GetCookieItemValue(key);
        } else if (storageType === StorageType.SessionStorage) {
            // console.log("storageType is sessionstorage");
            return sessionStorage.getItem(key);
        } else {
            // console.log("storageType is localstorage");
            return localStorage.getItem(key);
        }
    }

    public static GetLocalStorageItemCbpxUserNameAsString(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserName);
        return (val === null) ? "" : val.toString();
    }

    private static toBoolean(input: string): boolean {
        if (input === undefined || input === null) return false;
        if (input.toString().toLowerCase() === "true") return true;
        return false;
    }

    public static GetLocalStorageItemCpbxHideDecommissionDialog(): boolean {
        const val = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxHideDecommissionDialog);
        // console.log('GetLocalStorageItem', val, typeof val)
        return this.toBoolean(val);
    }

    public static GetLocalStorageItemCpbxHideVerhinderungAppNutzungDialog(): boolean {
        const val = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxHideVerhinderungAppNutzungDialog);
        // console.log('GetLocalStorageItem', val, typeof val)
        return this.toBoolean(val);
    }

    public static GetLocalStorageItemCpbxHideWelcomeDialog(): boolean {
        const val = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxHideWelcomeDialog);
        // console.log('GetLocalStorageItem', val, typeof val)
        return this.toBoolean(val);
    }

    public static getCurrentUserName(): string {
        const key = "CpbxProfileMeJson";
        const result = this.GetLocalStorageItem(key);
        // in case login was not correct, but localstorage entry has been set (to empty)
        if (result === null) return "";
        const resultArray = JSON.parse(result);
        return resultArray.Profile.details.firstName.$ + " " + resultArray.Profile.details.lastName.$;
    }

    public static GetLocalStorageItemCbpxUserBasicAuthStringAsString(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserBasicAuthString);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemCbpxUserProfileMeAsString(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserProfileMe);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemCbpxUserIVString(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserIvString);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemM365UserObjectId(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyM365UserObjectId);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemCpbxUserType(): string {
        const val: any = this.GetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserType);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemCbpxUserImpIdFromProfileMe(): string {
        const val: string = this.GetLocalStorageItemCbpxUserProfileMeAsString();
        if (val.length === 0) {
            return "";
        } else {
            const pMe = JSON.parse(val);
            if (pMe &&
                pMe.Profile &&
                pMe.Profile.additionalDetails &&
                pMe.Profile.additionalDetails.impId &&
                pMe.Profile.additionalDetails.impId.$) {
                return pMe.Profile.additionalDetails.impId.$;
            } else {
                return "";
            }
        }
    }

    public static GetLocalStorageItemCbpxUserNumberFromProfileMe(): string {
        const val: string = this.GetLocalStorageItemCbpxUserProfileMeAsString();
        if (val.length === 0) {
            return "";
        } else {
            const pMe = JSON.parse(val);
            if (pMe &&
                pMe.Profile &&
                pMe.Profile.details &&
                pMe.Profile.details.number &&
                pMe.Profile.details.number.$) {
                return pMe.Profile.details.number.$;
            } else {
                return "";
            }
        }
    }

    // will be used by WebexPresenceIcon (group-id is needed as http-request-header)
    public static GetLocalStorageItemCbpxUserGroupIdFromProfileMe(): string {
        const val: string = this.GetLocalStorageItemCbpxUserProfileMeAsString();
        if (val.length === 0) {
            return "";
        } else {
            const pMe = JSON.parse(val);
            if (pMe &&
                pMe.Profile &&
                pMe.Profile.details &&
                pMe.Profile.details.groupId &&
                pMe.Profile.details.groupId.$) {
                return pMe.Profile.details.groupId.$;
            } else {
                return "";
            }
        }
    }

    public static GetLocalStorageItemCbpxEnvName(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxEnvName, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemCbpxProxyUrl(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxProxyUrl, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemDataPrivStateLastUpdated(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyDataPrivStateLastUpdatedByUser, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageCiscoTokenForGroup(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCiscoTokenForGroup, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageAddressBookRecordsAndAtDateTimeUtc(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyAddressBookRecords, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    public static GetLocalStorageItemVersionSoftwareLastUpdated(): string {
        const val: any = this.GetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyUpdateInfoSoftwareLastUpdated, StorageType.LocalStorage);
        return (val === null) ? "" : val.toString();
    }

    private static SetLocalStorageItem(key: string, value: any): void {
        this.SetLocalStorageItemDynamic(key, value, StorageType.Dynamic);
    }

    public static SetLocalStorageItemDynamic(key: string, value: any, storageType: StorageType): void {
        console.log("SetLocalStorageItemDynamic [key='" + key + "', value='" + value + "', storageType='" + storageType.toString() + "']");
        if (storageType === StorageType.Cookie) {
            console.log("use cookie");
            this.SetCookieItem(key, value);
        } else if (storageType === StorageType.SessionStorage) {
            console.log("use sessionstorage");
            sessionStorage.setItem(key, value);
        } else {
            console.log("use localstorage");
            localStorage.setItem(key, value);
        }
    }

    public static SetLocalStorageItemCbpxUserName(value: string): void {
        this.SetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserName, value);
    }

    public static SetLocalStorageItemCpbxHideWelcomeDialog(value: boolean): void {
        this.SetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxHideWelcomeDialog, value);
    }

    public static SetLocalStorageItemCpbxHideDecommissionDialog(value: boolean): void {
        this.SetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxHideDecommissionDialog, value);
    }

    public static SetLocalStorageItemCbpxUserNameDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxUserName, value, storageType);
    }

    public static SetLocalStorageItemCbpxUserBasicAuthString(value: string): void {
        this.SetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserBasicAuthString, value);
    }

    public static SetLocalStorageItemCbpxUserBasicAuthStringDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxUserBasicAuthString, value, storageType);
    }

    public static SetLocalStorageItemCbpxUserIVStringDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxUserIvString, value, storageType);
    }

    public static SetLocalStorageItemM365UserObjectIdDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyM365UserObjectId, value, storageType);
    }

    public static SetLocalStorageItemCbpxUserProfileMe(value: string): void {
        this.SetLocalStorageItem(ConstantsBrowserLocalCache.KeyCpbxUserProfileMe, value);
    }

    public static SetLocalStorageItemCbpxUserProfileMeDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxUserProfileMe, value, storageType);
    }

    public static SetLocalStorageItemCbpxEnvName(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxEnvName, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageItemCbpxProxyUrl(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxProxyUrl, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageItemDataPrivStateLastUpdated(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyDataPrivStateLastUpdatedByUser, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageCiscoTokenForGroup(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCiscoTokenForGroup, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageAddressBookRecordsAndAtDateTimeUtc(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyAddressBookRecords, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageItemVersionSoftwareLastUpdated(value: string): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyUpdateInfoSoftwareLastUpdated, value, StorageType.LocalStorage);
    }

    public static SetLocalStorageItemCbpxUserTypeDynamic(value: string, storageType: StorageType): void {
        this.SetLocalStorageItemDynamic(ConstantsBrowserLocalCache.KeyCpbxUserType, value, storageType);
    }

    private static RemoveLocalStorageItem(key: string): void {
        this.RemoveLocalStorageItemDynamic(key, StorageType.Dynamic);
    }

    private static RemoveLocalStorageItemDynamic(key: string, storageType: StorageType): void {
        if (storageType === StorageType.Dynamic) {
            // try to remove from all storages
            this.RemoveCookieItem(key);
            sessionStorage.removeItem(key);
            localStorage.removeItem(key);
        } else if (storageType === StorageType.Cookie) {
            this.RemoveCookieItem(key);
        } else if (storageType === StorageType.SessionStorage) {
            sessionStorage.removeItem(key);
        } else {
            localStorage.removeItem(key);
        }
    }

    private static SetCookieItem(key: string, value: string): void {
        // console.log("start SetCookieItem [key='" + key + "', value='" + value + "']");

        // maybe we should check maximum length of value
        // allowed storage per cookie-item is 4096 bytes

        const encodedValue = encodeURIComponent(value);
        const completeEntry = key + "=" + encodedValue + ";path=/;SameSite=none;Secure";

        // console.log("completeEntry='" + completeEntry + "' (length=" + completeEntry.length + ")");

        document.cookie = completeEntry;
    }

    private static RemoveCookieItem(key: string): void {
        console.log("start RemoveCookieItem [key='" + key + "']");
        document.cookie = key + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=none;Secure";
    }

    private static GetCookieItem(key: string): string {
        // console.log("start GetCookieItem [key='" + key + "']");
        const searchFor = key + "=";
        // document.cookie: "testkey1=testvalue1; testkey2=testvalue 2"
        const allCookiesArr = document.cookie.split(";");
        for (let i = 0; i < allCookiesArr.length; i++) {
            let singleCookie = allCookiesArr[i];
            // remove starting spaces
            while (singleCookie.charAt(0) === " ") {
                singleCookie = singleCookie.substring(1);
            }
            // check if string starts with key
            if (singleCookie.indexOf(searchFor) === 0) {
                return singleCookie;
            }
        }
        return "";
    }

    private static DoesCookieKeyExists(key: string): boolean {
        // console.log("start DoesCookieKeyExists [key='" + key + "']");
        const cookieItem = this.GetCookieItem(key);
        // console.log("cookieItem='" + cookieItem + "'");

        const cookieExists: boolean = (cookieItem.length > 0);
        // console.log("cookieExists='" + cookieExists + "' [key='" + key + "']");
        return cookieExists;
    }

    private static GetCookieItemValue(key: string): string {
        // console.log("start GetCookieItem [key='" + key + "']");

        const cookieItem = this.GetCookieItem(key);
        if (cookieItem.length === 0) {
            // console.log("There exists no cookie with key='" + key + "'! Return empty string now.");
            return "";
        }

        // e.g. "testkey1=testvalue1"
        if (cookieItem.indexOf("=") === -1) {
            // console.log("CookieItem does not have a = character! This is nearly impossible! Return empty string now.");
            return "";
        }

        return decodeURIComponent(cookieItem.split("=")[1]);
    }
}
